import axios from "axios";

const API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? "/api"
    : process.env.REACT_APP_API_URL || "/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor: add auth Bearer token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("vortexbot_token");
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: handle 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.url?.includes("/auth/")) {
      localStorage.removeItem("vortexbot_token");
      window.dispatchEvent(new Event("auth_logout"));
    }
    return Promise.reject(error);
  }
);

export interface BotInfo {
  botStatus: string;
  botName: string;
  botId: string;
  botBanner: string;
  botAvatar: string;
  isVerified: boolean;
}

export interface BotStats {
  totalServers: number;
  totalUsers: number;
  totalCommands: number;
  botName: string;
  botIcon: string;
}

export interface BotStatus {
  online: boolean;
  uptime: number;
  ping: number;
  guilds: number;
  users: number;
  channels: number;
  timestamp: string;
}

export interface Guild {
  id: string;
  name: string;
  icon: string | null;
  memberCount: number;
  ownerId: string;
  verificationLevel: number;
  createdAt: string;
}

export interface Member {
  id: string;
  username: string;
  displayName: string;
  avatar: string;
  joinedAt: string;
  roles: Role[];
  status: string;
  isBot: boolean;
}

export interface Role {
  id: string;
  name: string;
  color: string;
}

export interface MusicTrack {
  title: string;
  author: string;
  duration: number;
  url?: string;
  thumbnail?: string;
  position?: number;
  requester?: string;
}

export interface MusicPlayerState {
  isPlaying: boolean;
  currentTrack?: MusicTrack | null;
  queue: MusicTrack[];
  volume: number;
  loop: string;
  paused: boolean;
  players?: any[];
  totalPlayers?: number;
}

export interface Command {
  name: string;
  description: string;
  category: string;
  cooldown?: number;
  adminOnly?: boolean;
  ownerOnly?: boolean;
  aliases?: string[];
}

export interface LogEntry {
  id: string;
  timestamp: string;
  level: string;
  message: string;
  error: string;
  stack: string;
  service: string;
}

export interface VoiceChannel {
  id: string;
  name: string;
  memberCount: number;
  bitrate: number;
  userLimit: number;
}

// ============ XP Types ============

export interface XPData {
  xp: number;
  level: number;
  progressPercent: number;
}

export interface GuildXPSettingsConfig {
  guildId?: string;
  enabled: boolean;
  xpPerMessage: number;
  cooldownSec: number;
  levelUpMessages: boolean;
  levelUpChannelId: string;
  roleRewards: Array<{ level: number; roleId: string }>;
}

export interface LeaderboardEntry {
  rank: number;
  userId: string;
  xp: number;
  level: number;
}

// ============ Welcome Types ============

export interface WelcomeSettingsConfig {
  guildId: string;
  welcomeEnabled: boolean;
  welcomeChannelId: string;
  messageTemplate: string;
  roleId: string;
  embedColor: string;
  embedImage: string;
  fieldLabel: string;
  fieldValue: string;
}

// ============ Twitch Types ============

export interface TwitchChannel {
  twitchName: string;
  discordChannelId: string;
}

// ============ Full Config Types ============

export interface FullConfig {
  prefix: string;
  logging: {
    guildJoinLogsId: string;
    guildLeaveLogsId: string;
    commandLogsChannelId: string;
    errorLogs: string;
  };
  fitgirlMonitor: {
    channelId: string;
    checkInterval: string;
    rssUrl: string;
  };
  bot: {
    id: string;
    ownerId: string;
    admins: string[];
  };
}

class ApiService {
  // Auth
  async login(password: string): Promise<{ success: boolean; token?: string; error?: string }> {
    try {
      const response = await api.post("/auth/login", { password });
      if (response.data.token) {
        localStorage.setItem("vortexbot_token", response.data.token);
      }
      return response.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error || "Password errata" };
    }
  }

  async verifyAuth(): Promise<boolean> {
    const token = localStorage.getItem("vortexbot_token");
    if (!token) return false;
    try {
      const response = await api.get("/auth/verify");
      return !!response.data.authenticated;
    } catch {
      return false;
    }
  }

  logout(): void {
    localStorage.removeItem("vortexbot_token");
    window.dispatchEvent(new Event("auth_logout"));
  }

  // Bot Information & Stats
  async getBotInfo(): Promise<BotInfo> {
    const response = await api.get<BotInfo>("/bot-info");
    return response.data;
  }

  async getBotStats(): Promise<BotStats> {
    const response = await api.get<BotStats>("/bot-stats");
    return response.data;
  }

  async updateBotProfile(formData: FormData): Promise<{ success: boolean; message?: string }> {
    const response = await api.post("/update-bot", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  }

  async setBotActivity(data: {
    status: string;
    activityName: string;
    activityType: string | number;
  }): Promise<{ success: boolean; message?: string }> {
    const response = await api.post("/activity", data);
    return response.data;
  }

  // Bot Status
  async getBotStatus(): Promise<BotStatus> {
    const response = await api.get<BotStatus>("/status");
    return response.data;
  }

  // Guilds
  async getGuilds(): Promise<Guild[]> {
    try {
      const response = await api.get<Guild[]>("/guilds");
      return response.data;
    } catch {
      return [];
    }
  }

  // Members
  async getGuildMembers(guildId: string): Promise<Member[]> {
    try {
      const response = await api.get<Member[]>(`/guilds/${guildId}/members`);
      return response.data;
    } catch {
      return [];
    }
  }

  // Channels + Roles dropdowns
  async getGuildChannels(
    guildId: string
  ): Promise<{ channels: { id: string; name: string; type: string }[]; roles: { id: string; name: string; color: string }[] }> {
    try {
      const response = await api.get(`/guilds/${guildId}/channels`);
      return response.data;
    } catch {
      return { channels: [], roles: [] };
    }
  }

  // Voice Channels
  async getVoiceChannels(guildId: string): Promise<VoiceChannel[]> {
    try {
      const response = await api.get<VoiceChannel[]>(`/guilds/${guildId}/channels/voice`);
      return response.data;
    } catch {
      return [];
    }
  }

  // Music Player
  async getMusicStatus(): Promise<MusicPlayerState> {
    try {
      const response = await api.get<MusicPlayerState>("/music/status");
      return response.data;
    } catch {
      return {
        isPlaying: false,
        queue: [],
        volume: 50,
        loop: "none",
        paused: false,
      };
    }
  }

  async playMusic(
    query: string,
    guildId: string,
    voiceChannelId: string,
    textChannelId: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.post("/music/play", {
      query,
      guildId,
      voiceChannelId,
      textChannelId,
    });
    return response.data;
  }

  async pauseMusic(guildId: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post("/music/pause", { guildId });
    return response.data;
  }

  async resumeMusic(guildId: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post("/music/resume", { guildId });
    return response.data;
  }

  async skipTrack(guildId: string, trackIndex?: number): Promise<{ success: boolean; message: string }> {
    const response = await api.post("/music/skip", { guildId, trackIndex });
    return response.data;
  }

  async stopMusic(guildId: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post("/music/stop", { guildId });
    return response.data;
  }

  async skipToTrack(guildId: string, trackIndex: number): Promise<{ success: boolean; message: string }> {
    const response = await api.post("/music/skipTo", { guildId, trackIndex });
    return response.data;
  }

  async setVolume(guildId: string, volume: number): Promise<{ success: boolean; message: string; volume: number }> {
    const response = await api.post("/music/volume", { guildId, volume });
    return response.data;
  }

  // Commands
  async getCommands(): Promise<{ slash: Command[]; prefix: Command[] }> {
    try {
      const response = await api.get<{ slash: Command[]; prefix: Command[] }>("/commands");
      return response.data;
    } catch {
      return { slash: [], prefix: [] };
    }
  }

  // Logs & Errors
  async getLogs(): Promise<LogEntry[]> {
    try {
      const response = await api.get<LogEntry[]>("/logs");
      return response.data;
    } catch {
      return [];
    }
  }

  async getErrors(): Promise<{ errors: Array<{ fileName: string; content: string }> }> {
    try {
      const response = await api.get("/errors");
      return response.data;
    } catch {
      return { errors: [] };
    }
  }

  // XP System
  async getXPData(guildId: string, userId: string): Promise<XPData> {
    try {
      const response = await api.get("/xp/data", { params: { guildId, userId } });
      return response.data;
    } catch {
      return { xp: 0, level: 1, progressPercent: 0 };
    }
  }

  async getLeaderboard(guildId: string): Promise<LeaderboardEntry[]> {
    try {
      const response = await api.get("/xp/leaderboard", { params: { guildId } });
      return response.data;
    } catch {
      return [];
    }
  }

  async getXPSettings(guildId: string): Promise<GuildXPSettingsConfig> {
    try {
      const response = await api.get("/xp/settings", { params: { guildId } });
      return response.data;
    } catch {
      return { enabled: true, xpPerMessage: 5, cooldownSec: 5, levelUpMessages: true, levelUpChannelId: "", roleRewards: [] };
    }
  }

  async updateXPSettings(guildId: string, settings: Partial<GuildXPSettingsConfig>): Promise<{ success: boolean }> {
    const response = await api.post("/xp/settings", { guildId, ...settings });
    return response.data;
  }

  // Welcome System
  async getWelcomeSettings(guildId: string): Promise<WelcomeSettingsConfig> {
    try {
      const response = await api.get("/welcome/settings", { params: { guildId } });
      return response.data;
    } catch {
      return {
        guildId,
        welcomeEnabled: false,
        welcomeChannelId: "",
        messageTemplate: "Benvenuto {user} nel server {guild}!",
        roleId: "",
        embedColor: "#037821",
        embedImage: "",
        fieldLabel: "Ottieni i tuoi ruoli qui:",
        fieldValue: "",
      };
    }
  }

  async updateWelcomeSettings(guildId: string, settings: Partial<WelcomeSettingsConfig>): Promise<{ success: boolean }> {
    const response = await api.post("/welcome/settings", { guildId, ...settings });
    return response.data;
  }

  async testWelcomeMessage(
    guildId: string,
    settings?: Partial<WelcomeSettingsConfig>
  ): Promise<{ success: boolean; message?: string }> {
    const response = await api.post("/welcome/test", { guildId, ...settings });
    return response.data;
  }

  // Twitch Channels
  async getTwitchChannels(guildId: string): Promise<{ monitoredChannels: TwitchChannel[] }> {
    try {
      const response = await api.get("/twitch/channels", { params: { guildId } });
      return response.data;
    } catch {
      return { monitoredChannels: [] };
    }
  }

  async updateTwitchChannels(guildId: string, channels: TwitchChannel[]): Promise<{ success: boolean }> {
    const response = await api.post("/twitch/channels", { guildId, monitoredChannels: channels });
    return response.data;
  }

  // Full Config
  async getFullConfig(): Promise<FullConfig> {
    const response = await api.get<FullConfig>("/config/full");
    return response.data;
  }

  async updateFullConfig(config: Partial<FullConfig>): Promise<{ success: boolean; message?: string }> {
    const response = await api.post("/config/full", config);
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
